import { getAnalyticsParams, sendEvent } from '@core/analytics'
import { DIALOG_IDS, modalStore } from '@core/modal'
import { useActions } from '@core/store'
import type { TGreetingKey } from '../constants/greetings'
import { authModalStore, type TAuthView } from '../store/authModalStore'
import { authIntentStore, type TAuthIntent } from '../store/authIntentStore'

type TOpenParams = {
    greetingKey?: TGreetingKey
    view?: TAuthView
    intent?: TAuthIntent | null
    modalType?: string
}

/**
 * Imperative open/close seam for the auth modal (mirror of `useGiftDialog`). Guest-gated seams call
 * `open({ greetingKey, intent })`; the resolved intent is replayed after successful auth by the
 * app-level `AuthIntentRunner`. `close` (user dismiss) clears the pending intent; the success path
 * closes via `modalStore` directly so the runner still sees the intent.
 */
export const useAuthModal = () => {
    const actions = useActions({
        setActiveView: authModalStore.actions.setActiveView,
        setEntryView: authModalStore.actions.setEntryView,
        setGreetingKey: authModalStore.actions.setGreetingKey,
        setModalType: authModalStore.actions.setModalType,
        setIsAuthSuccessful: authModalStore.actions.setIsAuthSuccessful,
        resetModal: authModalStore.actions.reset,
        setIntent: authIntentStore.actions.setIntent,
        clearIntent: authIntentStore.actions.clearIntent,
        armRegPaywall: authIntentStore.actions.armRegPaywall,
        clearRegPaywall: authIntentStore.actions.clearRegPaywall,
        openModal: modalStore.actions.open,
        closeModal: modalStore.actions.close,
    })

    const open = ({
        greetingKey = 'default',
        view = 'sign-in',
        intent = null,
        modalType,
    }: TOpenParams = {}) => {
        actions.setGreetingKey(greetingKey)
        actions.setActiveView(view)
        actions.setEntryView(view)
        actions.setModalType(modalType ?? null)
        actions.setIsAuthSuccessful(false)
        actions.setIntent({ intent, at: Date.now() })
        actions.clearRegPaywall()
        actions.openModal(DIALOG_IDS.auth)
        sendEvent({
            name: 'auth_modal_show',
            payload: getAnalyticsParams({ modal_type: modalType }),
        })
    }

    const close = () => {
        actions.closeModal()
        actions.resetModal()
        actions.clearIntent()
        actions.clearRegPaywall()
    }

    const setView = (view: TAuthView) => {
        actions.setActiveView(view)
    }

    return { open, close, setView }
}
